import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Icon } from '@artprov/design-system'
import { useEscapeKey } from '../hooks/useEscapeKey'
import { cn } from '../../_utils/cn'

const menuNodeShape = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  nombre: PropTypes.string.isRequired,
  orden: PropTypes.number,
  url: PropTypes.string,
}

const EMPTY_FOOTER_ITEMS = []

const sortItems = (items) => [...items].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))

/**
 * Navegación mobile que muestra un único nivel por vez.
 * @param {import('../Menu.types').MenuDrawerProps} props
 */
export function MenuDrawer({
  items,
  isOpen,
  initialFooterItemId,
  onOpenChange,
  onSelect,
  footerItems = EMPTY_FOOTER_ITEMS,
  className,
  dataTestId,
}) {
  const [path, setPath] = useState([])
  const [activeFooterItem, setActiveFooterItem] = useState(
    () => footerItems.find((item) => item.id === initialFooterItemId && item.renderContent) ?? null
  )
  const currentNode = path.at(-1)
  const currentItems = useMemo(() => sortItems(currentNode?.children ?? items), [currentNode, items])

  const close = () => {
    setActiveFooterItem(null)
    onOpenChange?.(false)
  }
  useEscapeKey(close, isOpen)

  if (!isOpen) return null

  const handleItemClick = (item) => {
    if (item.children?.length) {
      setPath((currentPath) => [...currentPath, item])
      return
    }

    onSelect?.(item)
    close()
  }

  const handleBack = () => {
    if (activeFooterItem) {
      setActiveFooterItem(null)
      return
    }

    setPath((currentPath) => currentPath.slice(0, -1))
  }

  const currentTitle = activeFooterItem?.label ?? currentNode?.nombre

  return (
    <nav
      id={dataTestId}
      aria-label="Menú principal"
      className={cn('flex min-h-[calc(100dvh-72px)] w-full flex-col bg-tertiary-100', className)}
      data-testid={dataTestId}
    >
      {currentTitle ? (
        <button
          type="button"
          onClick={handleBack}
          className="flex h-[52px] w-full items-center gap-3 bg-primary-95 px-6 text-primary-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-70"
          aria-label={`Volver desde ${currentTitle}`}
          data-testid={`${dataTestId}-back`}
        >
          <Icon icon="keyboard_arrow_left" size="medium" />
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-none tracking-[0.1px]">
            {currentTitle}
          </span>
        </button>
      ) : (
        <div className="flex h-[52px] w-full items-center bg-primary-95 px-6 text-primary-10">
          <span className="text-sm font-semibold leading-none tracking-[0.1px]">Menú principal</span>
        </div>
      )}

      {activeFooterItem ? (
        <div className="flex flex-1">{activeFooterItem.renderContent({ close })}</div>
      ) : (
        <div className="flex flex-1 flex-col gap-3 px-3 py-5" role="menu">
          {currentItems.map((item) => {
            const hasChildren = Boolean(item.children?.length)

            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                aria-haspopup={hasChildren ? 'menu' : undefined}
                onClick={() => handleItemClick(item)}
                className="flex min-h-[42px] w-full items-center rounded-[4px] px-3 text-left text-primary-10 hover:bg-primary-98 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-70"
                data-testid={`${dataTestId}-item-${item.id}`}
              >
                <span className="flex-1 text-sm font-semibold leading-none tracking-[0.1px]">{item.nombre}</span>
                {hasChildren ? <Icon icon="chevron_forward" size="small" /> : null}
              </button>
            )
          })}
        </div>
      )}

      {!activeFooterItem && footerItems.length > 0 ? (
        <div className="bg-tertiary-98 px-3 py-5" aria-label="Acciones de usuario">
          {footerItems.map((item) => (
            <div key={item.id} className={item.dividerBefore ? 'mt-3 border-t border-tertiary-80 pt-3' : undefined}>
              <button
                type="button"
                onClick={() => {
                  if (item.renderContent) {
                    setActiveFooterItem(item)
                    return
                  }

                  if (!item.onSelect) return

                  item.onSelect()
                  close()
                }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-[4px] px-3 text-left text-primary-10 hover:bg-primary-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-70"
                data-testid={`${dataTestId}-footer-${item.id}`}
              >
                {item.icon ? <Icon icon={item.icon} size="medium" /> : null}
                <span className="flex-1 text-sm font-semibold leading-none tracking-[0.1px]">{item.label}</span>
                {item.showChevron ? <Icon icon="chevron_forward" size="small" /> : null}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </nav>
  )
}

MenuDrawer.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      ...menuNodeShape,
      children: PropTypes.array,
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  initialFooterItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOpenChange: PropTypes.func,
  onSelect: PropTypes.func,
  footerItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onSelect: PropTypes.func,
      dividerBefore: PropTypes.bool,
      showChevron: PropTypes.bool,
      renderContent: PropTypes.func,
    })
  ),
  className: PropTypes.string,
  dataTestId: PropTypes.string,
}
